import { Injectable } from '@angular/core';
import { ProductSiteRegionService } from '@app/services/product-site-region.service';
import { TfUserModel } from '../models/tf-user.model';
import { TfWindowRefService } from '../../tf-window-ref';
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, finalize, first, map, Observable, skipWhile } from "rxjs";

@Injectable({
    providedIn: "root"
})
export class TfUserDataService {

    comergentHost: string = "";
    comergentBasePath: string = "";
    loginURL: string = "";
    redirectURL: string = "";
    useKong: boolean = true;
    window: any;

    processing: boolean = false;
    timeout: number = 5 * 60 * 1000; // 5 minutes in seconds.
    interval = null;

    user: TfUserModel = null;
    isLoggedInValue: boolean = false;

    userBS: BehaviorSubject<TfUserModel> = new BehaviorSubject<TfUserModel>(null);
    userObservable: Observable<TfUserModel>;

    constructor(
        private winRef: TfWindowRefService,
        private http: HttpClient,
        private productSiteRegionService: ProductSiteRegionService
    ) {

        this.window = this.winRef.nativeWindow;

        this.userObservable = this.makeUserObservable();

    }

    makeUserObservable() {

        return new Observable<TfUserModel>(subscriber => {

            // Only in Thermofisher we have this endpoint to query the user data
            if( this.productSiteRegionService.isThermofisher() ) {

                this.retrieveUserData();
                this.setInterval();

                this.userBS
                    .pipe(
                        skipWhile((value) => value == null))
                    .subscribe(value => {
                        subscriber.next(value);
                    })
            }

        });

    }

    login(): void {

        const commergentHost = this.window?.$m?.magellanConfig?.comergentHost;
        const commergentBasePath = this.window?.$m?.magellanConfig?.comergentBasePath;

        if (commergentHost && commergentBasePath) {
            this.comergentHost = this.window.$m['magellanConfig']['comergentHost'];
            this.comergentBasePath = this.window.$m['magellanConfig']['comergentBasePath'];
            this.loginURL = 'https://' + this.comergentHost + this.comergentBasePath + 'LoginRequired';
        } else {
            this.loginURL = '/auth/login?';
        }

        this.redirectURL = this.loginURL + 'returnUrl=' + this.window.location.href;
        this.window.location.href = this.redirectURL;

    }

    getUserDetails(): Observable<TfUserModel> {
        return this.userObservable.pipe(first());
    }

    getUserDetailsObservable(): Observable<TfUserModel> {
        return this.userObservable;
    }

    getUserFromLTObj(): TfUserModel {

        return new TfUserModel({
            isLoggedIn: this.isLoggedInValue,
            countryCode: this.getCountryCode(),
            currencyCode: this.getCurrencyCode(),
            shipToNumber: this.getShipToNumber(),
            languageCode: this.getLanguageCode(),
            isAnonymous: !this.isLoggedInValue
        });

    }

    getCurrencyCode() {
        return this.window?._lt?.user?.currencyCode || "USD";
    }

    getCountryCode() {
        return this.window?._lt?.user?.displayIso || "us";
    }

    getLanguageCode() {
        return this.window?._lt?.user?.displayLang || "en";
    }

    getShipToNumber() {
        if(this.userBS.getValue() != null) {
            return this.userBS.getValue().shipToNumber;
        }
        return null;
    }

    getIsLoggedInValue(): boolean {

        if( this.productSiteRegionService.isThermofisher() ) {
            this.retrieveUserData();
            this.setInterval();
        }

        if( this.productSiteRegionService.isFisherSci() ) {
            const userTypeFromCookie = document.cookie.match(/(^| )usertype=([^;]+)/);

            //M and R are logged in, G is guest.
            this.isLoggedInValue = userTypeFromCookie ? (userTypeFromCookie[2] === 'M' || userTypeFromCookie[2] === 'R') : false;
        }

        return this.isLoggedInValue;

    }

    private setInterval() {

        if (this.interval != null) {
            return;
        }

        this.interval = setInterval(() => {
            this.retrieveUserData();
        }, this.timeout);

    }

    private retrieveUserData() {

        if (this.user != null || this.processing) {
            return;
        }

        this.processing = true;

        this.retrieveUserDataFromAPI()
            .pipe(
                finalize(() => this.processing = false)
            )
            .subscribe({
                next: (user) => {
                    this.user = user;
                    this.isLoggedInValue = this.user.isLoggedIn;
                    this.userBS.next(this.user);
                },
                error: () => {
                    this.user = this.getUserFromLTObj();
                    this.isLoggedInValue = this.user.isLoggedIn;
                    this.userBS.next(this.user);
                }
            });

    }

    private retrieveUserDataFromAPI(): Observable<TfUserModel> {

        return this.http.get<TfUserModel>('/api/store/user-type')
            .pipe(
                map((res) => {
                    const user = res['data'];
                    const userBase = {};
                    if (user.userId) userBase['userId'] = user.userId;
                    if (user.userName) userBase['userName'] = user.userName;
                    if (user.isAnonymous) userBase['isAnonymous'] = user.isAnonymous;
                    if (user.isB2BMasterUser) userBase['isB2BMasterUser'] = user.isB2BMasterUser;
                    if (user.userType) userBase['userType'] = user.userType;
                    if (user.isCrmManager) userBase['isCrmManager'] = user.isCrmManager;
                    if (user.isSosManager) userBase['isSosManager'] = user.isSosManager;
                    if (user.shipTo) {
                        userBase['shipToNumber'] = user.shipTo;
                    } else {
                        userBase['shipToNumber'] = this.window?._lt?.user?.shipToNumber;
                    }
                    userBase['countryCode'] = this.getCountryCode();
                    userBase['currencyCode'] = this.getCurrencyCode();
                    userBase['languageCode'] = this.getLanguageCode();
                    userBase["isLoggedIn"] = !(userBase['isAnonymous'] === true);
                    return new TfUserModel(userBase);
                })
            );

    }

}
